import { Button } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";

export function BadgePage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "badge", filters.locale],
		queryFn: () => api.badge(entityId, filters.locale),
		...read,
	});

	async function copy(value: string) {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(t("badge.copied"));
		} catch {
			// Clipboard access can be refused; the snippet is selectable anyway.
		}
	}

	return (
		<StageShell entityId={entityId} reading="badge" lede={t("badge.lede")} filters={filters} setFilters={setFilters}>
			<Async state={state}>
				{(badge) => (
					<div style={{ display: "flex", flexDirection: "column", gap: "var(--s-10)" }}>
						<section className="panel">
							{/* Preview through the API so the console does not depend on the public site being up. */}
							<img
								src={`/api/v1/public/badge/${encodeURIComponent(badge.token)}.svg?locale=${encodeURIComponent(filters.locale)}`}
								alt={t("badge.preview")}
								height={28}
							/>
							<p className="faint" style={{ marginTop: "var(--s-7)" }}>
								{badge.backlink_ok === null
									? t("badge.backlinkUnchecked")
									: badge.backlink_ok
										? t("badge.backlinkOk")
										: t("badge.backlinkMissing")}
								{badge.backlink_checked_at ? ` · ${fmt.relative(locale, badge.backlink_checked_at)}` : ""}
							</p>
						</section>

						<Snippet
							label={t("badge.html")}
							value={badge.html}
							onCopy={() => copy(badge.html)}
							copyLabel={t("badge.copy")}
						/>
						<Snippet
							label={t("badge.markdown")}
							value={badge.markdown}
							onCopy={() => copy(badge.markdown)}
							copyLabel={t("badge.copy")}
						/>
					</div>
				)}
			</Async>
		</StageShell>
	);
}

function Snippet({
	label,
	value,
	onCopy,
	copyLabel,
}: {
	label: string;
	value: string;
	onCopy: () => void;
	copyLabel: string;
}) {
	return (
		<section className="panel">
			<div className="row-actions" style={{ justifyContent: "space-between", marginBottom: "var(--s-6)" }}>
				<h2 style={{ margin: 0 }}>{label}</h2>
				<Button type="button" variant="default" onClick={onCopy}>
					{copyLabel}
				</Button>
			</div>
			<pre>{value}</pre>
		</section>
	);
}
